import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StoreIcon from '@mui/icons-material/Store';
import LinkIcon from '@mui/icons-material/Link';
import api from '../services/api';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'user',
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'user' });
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        const data = { ...formData };
        if (!data.password) {
          delete data.password;
        }
        await api.put(`/users/${editingUser.id}`, data);
      } else {
        await api.post('/users', formData);
      }
      handleCloseDialog();
      loadUsers();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      console.error('Detalhes do erro:', error.response?.data);
      
      let errorMessage = 'Erro ao salvar usuário';
      
      if (error.response?.data) {
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = error.response.data.errors.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Erro ao salvar usuário:\n\n${errorMessage}${error.response?.data?.details ? '\n\nDetalhes: ' + JSON.stringify(error.response.data.details) : ''}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este usuário?')) {
      try {
        await api.delete(`/users/${id}`);
        loadUsers();
      } catch (error) {
        console.error('Erro ao deletar usuário:', error);
        alert('Erro ao deletar usuário');
      }
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'store_moderator':
        return 'secondary';
      case 'merchant':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'store_moderator':
        return 'Moderador da Loja';
      case 'merchant':
        return 'Lojista';
      default:
        return 'Usuário';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4, pt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Gerenciar Usuários
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<StoreIcon />}
            onClick={() => {
              setFormData({ name: '', email: '', password: '', role: 'merchant' });
              setEditingUser(null);
              setOpenDialog(true);
            }}
            sx={{ borderColor: 'warning.main', color: 'warning.main' }}
          >
            Novo Lojista
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Usuário
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Perfil</TableCell>
              <TableCell>Loja Vinculada</TableCell>
              <TableCell>Data de Cadastro</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={getRoleLabel(user.role)}
                    color={getRoleColor(user.role)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {(user.role === 'merchant' || user.role === 'store_moderator') ? (
                    user.store_name ? (
                      <Tooltip title={`Clique para gerenciar lojas. Loja ID: ${user.store_id}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LinkIcon fontSize="small" color="action" />
                          <Chip
                            label={user.store_name}
                            color="success"
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/admin/stores`)}
                            sx={{ cursor: 'pointer' }}
                          />
                          {user.is_moderator && (
                            <Chip
                              label="👑 Moderador"
                              size="small"
                              sx={{ 
                                backgroundColor: 'primary.main', 
                                color: '#ffffff',
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Este usuário ainda não está vinculado a uma loja. Vá para a página de Lojas para vincular.">
                        <Chip
                          label="Não vinculado"
                          color="warning"
                          size="small"
                          variant="outlined"
                          onClick={() => navigate('/admin/stores')}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString('pt-BR')}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(user)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(user.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser 
            ? 'Editar Usuário' 
            : formData.role === 'merchant' 
              ? 'Novo Lojista'
              : formData.role === 'store_moderator'
              ? 'Novo Moderador da Loja'
              : 'Novo Usuário'}
        </DialogTitle>
        <DialogContent>
          {!editingUser && (formData.role === 'merchant' || formData.role === 'store_moderator') && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Próximo passo:</strong> Após criar o {formData.role === 'store_moderator' ? 'moderador' : 'lojista'}, vá para a página de Lojas e vincule este usuário a uma loja.
                {formData.role === 'store_moderator' && ' Na página de Lojas, você pode definir este usuário como moderador da loja.'}
              </Typography>
            </Alert>
          )}
          <TextField
            fullWidth
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
            required={!editingUser}
            helperText={editingUser ? 'Deixe em branco para manter a senha atual' : 'Mínimo de 6 caracteres'}
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Perfil</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              label="Perfil"
            >
              <MenuItem value="user">Usuário</MenuItem>
              <MenuItem value="merchant">Lojista</MenuItem>
              <MenuItem value="store_moderator">Moderador da Loja</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.name || !formData.email || (!editingUser && !formData.password)}
          >
            {editingUser ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsers;
